import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Compares two session objects to determine if they represent a meaningful change.
 * This prevents unnecessary re-renders when Supabase fires auth state changes
 * during session recovery (e.g., when switching back to the tab in Safari).
 */
const hasSessionChanged = (prevUser, newUser) => {
  // If both are null/undefined, no change
  if (!prevUser && !newUser) return false;

  // If one is null and the other isn't, that's a change
  if (!prevUser || !newUser) return true;

  // Compare user IDs - if different, it's a real change
  if (prevUser.id !== newUser.id) return true;

  // Compare email (in case of email change)
  if (prevUser.email !== newUser.email) return true;

  // No meaningful change detected
  return false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Track current user ID to prevent unnecessary profile reloads
  const currentUserIdRef = useRef(null);

  useEffect(() => {
    // Check active session on mount
    checkUser();

    // Listen for auth changes
    const { data: authListener } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        const newUser = session?.user ?? null;

        // Only update state if there's a meaningful change
        // This prevents re-renders during Safari's aggressive session recovery
        setUser((prevUser) => {
          if (hasSessionChanged(prevUser, newUser)) {
            // Actual auth change - update profile accordingly
            if (newUser && newUser.id !== currentUserIdRef.current) {
              currentUserIdRef.current = newUser.id;
              loadProfile(newUser.id);
            } else if (!newUser) {
              currentUserIdRef.current = null;
              setProfile(null);
            }
            return newUser;
          }
          // No meaningful change - return previous user to skip re-render
          return prevUser;
        });
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      if (sessionUser) {
        currentUserIdRef.current = sessionUser.id;
        await loadProfile(sessionUser.id);
      }
    } catch (error) {
      console.error('Error checking user session:', error);
      setError(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      setError(error?.message);
    }
  };

  const signIn = async (email, password) => {
    try {
      setError('');
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      setError(error?.message);
      throw error;
    }
  };

  const signUp = async (email, password, fullName, username, accountType = 'corporate') => {
    try {
      setError('');
      const masterId = `${username?.toLowerCase()}@mervo`;
      
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            username: username?.toLowerCase(),
            master_id: masterId,
            account_type: accountType
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      setError(error?.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setError('');
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      currentUserIdRef.current = null;
      setUser(null);
      setProfile(null);
    } catch (error) {
      setError(error?.message);
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      setError('');
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

      if (error) throw error;
      setProfile(data);
      return data;
    } catch (error) {
      setError(error?.message);
      throw error;
    }
  };

  // Refresh profile from database (useful after avatar upload)
  const refreshProfile = async () => {
    if (user?.id) {
      await loadProfile(user.id);
    }
  };

  const value = {
    user,
    profile,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
